import {
  IShareInvoiceGenerateSetupIntentResp,
  QuoteShareResponse,
  QuoteStatusEnum,
  RecordShareEntity,
  RuleActionTypeEnum,
} from '@monetize/types/app';
import {
  ExperienceConfigurationKeys,
  IAnonymousSettingsConfigQuoteShareSchema,
  QuoteShareFieldVisibilityEnum,
} from '@monetize/types/experience-config';
import isString from 'lodash/isString';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import {
  useGetQuoteShare,
  usePrintQuoteShare,
  useSetupIntentForQuoteShare,
} from '../../api/shareService';
import {
  recordShareSelector,
  recordShareState,
} from '../../components/RecordShare/record-share.utils';
import { useRecordShareTrackView } from '../../components/RecordShare/useRecordShareTrackView';
import { useAnonymousExperienceConfiguration } from '../../hooks/useExperienceConfiguration';
import Sentry from '../../services/sentry';
import {
  getRecordShareCaptureContext,
  getRecordShareErrorLogContext,
} from '../../utils/recordShareLogging';
import {
  AllowedPaymentMethods,
  electronicPaymentMethods,
} from './quote-share.utils';

// Re-exported for existing importers; the enum now lives in quote-share.utils to
// avoid a circular import (quote-share.utils builds Sets from it at module load).
export { AllowedPaymentMethods };

const messages: Record<string, string> = {
  default:
    'There was a problem processing your request. Ask us to share the quote again.',
  404: 'This quote share link has expired. Ask us to share the quote again.',
  403: 'You are not permitted to view this quote, discuss with us to determine if they can share the quote again.',
};

/**
 * Sentry buffers logs on an interval and the browser SDK does not flush on
 * unload, so the hard reload below would otherwise discard the report we just
 * captured. `flush()` drains the buffer immediately; this timeout only caps how
 * long we wait for the transport before reloading anyway.
 */
const SENTRY_FLUSH_TIMEOUT_MS = 2000;

export type QuoteShareUxConfig = {
  requireContactAddress: boolean;
  requirePaymentMethod: boolean;
  allowedPaymentMethods: AllowedPaymentMethods;
  achContent: string | null | undefined;
  purchaseOrderNumberVisibility: QuoteShareFieldVisibilityEnum;
  vatNumberVisibility: QuoteShareFieldVisibilityEnum;
  allowAccountNameUpdate: boolean;
  collectPaymentBeforeQuoteProcessing?: boolean;
};

const QUOTE_SHARE_FIELD_VISIBILITY_VALUES = new Set<string>(
  Object.values(QuoteShareFieldVisibilityEnum),
);

function isQuoteShareFieldVisibility(
  value: unknown,
): value is QuoteShareFieldVisibilityEnum {
  return (
    typeof value === 'string' && QUOTE_SHARE_FIELD_VISIBILITY_VALUES.has(value)
  );
}

/**
 * Resolves the effective Quote Share UX config by layering Record Automation
 * rule overrides on top of the tenant-level config. When a setting is present in
 * both places, the rule value wins (Record Automation takes precedence).
 */
export function resolveQuoteShareConfig(
  quoteShareConfig: IAnonymousSettingsConfigQuoteShareSchema,
  rules: QuoteShareResponse['rules'],
  hasPaymentGatewayConfigured: boolean,
) {
  const output: QuoteShareUxConfig = {
    requireContactAddress: !!quoteShareConfig.contact.requireAddress,
    requirePaymentMethod:
      hasPaymentGatewayConfigured && !!quoteShareConfig.paymentMethod.required,
    // Only applicable if requirePaymentMethod is true
    allowedPaymentMethods: AllowedPaymentMethods.ELECTRONIC,
    achContent: quoteShareConfig.paymentMethod.manualACH.content,
    purchaseOrderNumberVisibility:
      quoteShareConfig.purchaseOrderNumber.visibility,
    vatNumberVisibility: quoteShareConfig.vatNumber.visibility,
    allowAccountNameUpdate: !!quoteShareConfig.accountName.allowUpdate,
  };

  if (
    quoteShareConfig.paymentMethod.manualACH.enabled &&
    quoteShareConfig.paymentMethod.manualACH.content
  ) {
    output.allowedPaymentMethods = AllowedPaymentMethods.ELECTRONIC_AND_ACH;
    output.achContent = quoteShareConfig.paymentMethod.manualACH.content;
  }

  if (rules?.status !== 'SUCCESS' || !rules?.actions.length) {
    return output;
  }

  rules.actions.forEach((action) => {
    if (action.type !== RuleActionTypeEnum.RECORD_AUTOMATION) {
      return;
    }
    switch (action.recordUpdate.targetField) {
      case 'requireContactAddress':
        output.requireContactAddress = action.recordUpdate.value === true;
        break;
      case 'requirePaymentMethod':
        // A rule cannot require a payment method when no gateway is configured —
        // the payment card would never render and acceptance would deadlock.
        output.requirePaymentMethod =
          hasPaymentGatewayConfigured && action.recordUpdate.value === true;
        break;
      case 'allowedPaymentMethods':
        output.allowedPaymentMethods = action.recordUpdate
          .value as AllowedPaymentMethods;
        break;
      case 'achPaymentInstructionsOverride':
        if (isString(action.recordUpdate.value)) {
          output.achContent = action.recordUpdate.value as string;
        }
        break;
      case 'purchaseOrderNumberVisibility':
        if (isQuoteShareFieldVisibility(action.recordUpdate.value)) {
          output.purchaseOrderNumberVisibility = action.recordUpdate.value;
        }
        break;
      case 'vatNumberVisibility':
        if (isQuoteShareFieldVisibility(action.recordUpdate.value)) {
          output.vatNumberVisibility = action.recordUpdate.value;
        }
        break;
      case 'allowAccountNameUpdate':
        output.allowAccountNameUpdate = action.recordUpdate.value === true;
        break;
      case 'collectPaymentBeforeQuoteProcessing':
        output['collectPaymentBeforeQuoteProcessing'] =
          hasPaymentGatewayConfigured && action.recordUpdate.value === true;
        break;
    }
  });

  return output;
}

export function useQuoteShareData() {
  const recordShareAuthData = useAtomValue(recordShareSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const resetRecordShareState = useResetAtom(recordShareState);

  const { quoteShareConfig } = useAnonymousExperienceConfiguration(
    recordShareAuthData.tenantId,
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );

  useRecordShareTrackView(
    recordShareAuthData.tenantId,
    recordShareAuthData.recordId,
    recordShareAuthData.token,
    RecordShareEntity.QUOTE,
  );

  const [stripeKeys, setStripeKeys] =
    useState<IShareInvoiceGenerateSetupIntentResp>();

  // Holds the rejection itself, not just a flag, so the failure can be reported
  // with a status and a message instead of a bare "something went wrong".
  const [paymentIntentError, setPaymentIntentError] = useState<unknown>(null);
  const hasPaymentIntentError = !!paymentIntentError;

  const {
    data: quoteData,
    isLoading: isLoadingQuote,
    isError: quoteDataHasError,
    error: quoteShareError,
  } = useGetQuoteShare(recordShareAuthData, {
    refetchOnWindowFocus: false,
  });

  const {
    data: quotePdfData,
    isLoading: isLoadingPdf,
    isError: quotePdfHasError,
    error: quotePdfError,
  } = usePrintQuoteShare(recordShareAuthData, {
    enabled: !!quoteData?.quote?.id,
    refetchOnWindowFocus: false,
  });

  const hasPaymentGatewayConfigured = !!quoteData?.paymentGatewayId;

  const quoteShareUxConfig = resolveQuoteShareConfig(
    quoteShareConfig,
    quoteData?.rules,
    hasPaymentGatewayConfigured,
  );

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  const [lockPaymentMethod, setLockPaymentMethod] = useState(false);
  const [paymentType, setPaymentType] = useState<'ELECTRONIC' | 'ACH'>(() =>
    electronicPaymentMethods.has(quoteShareUxConfig.allowedPaymentMethods)
      ? 'ELECTRONIC'
      : 'ACH',
  );

  const { mutateAsync: generateSetupIntent } = useSetupIntentForQuoteShare();

  useEffect(() => {
    setPaymentType(
      electronicPaymentMethods.has(quoteShareUxConfig.allowedPaymentMethods)
        ? 'ELECTRONIC'
        : 'ACH',
    );
    setLockPaymentMethod(
      quoteShareUxConfig.allowedPaymentMethods ===
        AllowedPaymentMethods.ACH_WIRE_TRANSFER,
    );
  }, [quoteShareUxConfig.allowedPaymentMethods]);

  // A Stripe SetupIntent can only be confirmed once. Generating a fresh intent
  // lets a user add another payment method without refreshing the page (the
  // previous intent is already "succeeded" and would be rejected by Stripe).
  const refreshSetupIntent = useCallback(() => {
    if (
      !quoteData?.paymentGatewayId ||
      quoteData.quote.status !== QuoteStatusEnum.APPROVED
    ) {
      return;
    }
    generateSetupIntent({
      ...recordShareAuthData,
      paymentGatewayId: quoteData.paymentGatewayId,
    })
      .then((keys) => setStripeKeys(keys))
      .catch((error) =>
        setPaymentIntentError(
          error || new Error('Failed to create payment intent'),
        ),
      );
  }, [
    generateSetupIntent,
    quoteData?.paymentGatewayId,
    quoteData?.quote?.status,
    recordShareAuthData,
  ]);

  // initialize payment setup intent
  useEffect(() => {
    refreshSetupIntent();
  }, [refreshSetupIntent]);

  useEffect(() => {
    if (
      !!quoteData?.paymentMethods?.length &&
      electronicPaymentMethods.has(quoteShareUxConfig.allowedPaymentMethods)
    ) {
      setSelectedPaymentMethodId(
        quoteData.paymentMethods.find(({ isDefault }) => isDefault)?.id ||
          quoteData.paymentMethods[0].id,
      );
    }
  }, [quoteData?.paymentMethods, quoteShareUxConfig.allowedPaymentMethods]);

  const dataLoadingCriticalError = quoteShareError || quotePdfError;
  // The PDF is fetched from a different endpoint, so knowing which one failed is
  // what separates a genuinely bad token from a transient /print blip.
  const dataLoadingErrorSource = quoteShareError ? 'quote' : 'pdf';

  useEffect(() => {
    if (dataLoadingCriticalError) {
      const logContext = getRecordShareErrorLogContext(
        recordShareAuthData,
        dataLoadingCriticalError,
      );

      Sentry.logger.error('QuoteSharePage - data loading failed', {
        ...logContext,
        source: dataLoadingErrorSource,
      });
      // Sentry is not initialized on localhost at all, so this needs no
      // environment gate — production failures are the ones worth an issue.
      Sentry.captureException(
        dataLoadingCriticalError,
        getRecordShareCaptureContext(logContext, {
          source: dataLoadingErrorSource,
        }),
      );

      resetRecordShareState();

      const errorMessage = messages[logContext.status] || messages.default;
      const params = new URLSearchParams(searchParams);
      params.set('error', errorMessage);
      setSearchParams(params);

      const reloadAfterFlush = async () => {
        try {
          await Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS);
        } catch {
          // Never block the reload on the flush
        }
        location.reload();
      };
      void reloadAfterFlush();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoadingCriticalError]);

  // Log payment intent errors to sentry
  useEffect(() => {
    if (!paymentIntentError) {
      return;
    }
    const logContext = getRecordShareErrorLogContext(
      recordShareAuthData,
      paymentIntentError,
    );
    Sentry.logger.warn('QuoteSharePage - payment intent error', logContext);
    // Wrapped so every setup-intent failure groups under one Sentry issue; the
    // cause is chained so the underlying axios error is still on the event.
    Sentry.captureException(
      new Error('Error creating payment intent', {
        cause: paymentIntentError,
      }),
      getRecordShareCaptureContext(logContext, {
        action: 'CREATE_SETUP_INTENT',
      }),
    );
  }, [paymentIntentError, recordShareAuthData]);

  const shouldCreateSetupIntent =
    quoteData?.paymentGatewayId &&
    quoteData.quote.status === QuoteStatusEnum.APPROVED;

  // Ensure that the UI does not "flash" between loading and rendered state since
  // we use a useEffect to initiate the fetching of the setup intent. This only
  // gates the initial load — regenerating the intent after a card is added
  // happens in the background (stripeKeys stays populated) so the page does not
  // flash back to a loading state.
  const isLoadingSetupIntent =
    shouldCreateSetupIntent && !hasPaymentIntentError && !stripeKeys;

  return {
    isLoading: isLoadingQuote || isLoadingSetupIntent,
    isLoadingPdf,
    isLoadingSetupIntent,
    stripeKeys,
    paymentIntentError: hasPaymentIntentError,
    hasPaymentGatewayConfigured,
    quoteData,
    quoteDataHasError,
    quotePdfData,
    quotePdfHasError,
    recordShareAuthData,
    quoteShareUxConfig,
    lockPaymentMethod,
    paymentType,
    setPaymentType,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    refreshSetupIntent,
  };
}
